import React from "react";
import PaymentDialogClose from "./PaymentDialogClose";
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
} from "./ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import ActionButton from "./base/ActionButton";
import { RiLoader4Line } from "react-icons/ri";
import "./PayHandoffModal.css";

const LoaderIcon = RiLoader4Line as unknown as React.ComponentType<{
  size?: number;
  className?: string;
}>;

interface ParticipantSummary {
  id: string;
  name: string;
}

interface PaymentConfirmationModalProps {
  isOpen: boolean;
  selectedParticipantIds: string[];
  participants: ParticipantSummary[];
  isConfirmingPaymentComplete: boolean;
  onConfirmPaymentComplete: () => void;
  onClose: () => void;
}

export default function PaymentConfirmationModal({
  isOpen,
  selectedParticipantIds,
  participants,
  isConfirmingPaymentComplete,
  onConfirmPaymentComplete,
  onClose,
}: PaymentConfirmationModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogPortal>
        <DialogOverlay className="z-[1300]" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-[1300] w-[calc(100%-32px)] max-w-sm max-h-[calc(100dvh-32px)] overflow-y-auto translate-x-[-50%] translate-y-[-50%] rounded-[20px] border border-border/80 bg-background p-5 shadow-lg transition-opacity duration-200 data-[state=open]:opacity-100 data-[state=closed]:opacity-0">
          <DialogPrimitive.Title className="mb-2 text-left text-lg font-bold text-slate-900">
            Mark as paid?
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="mb-4 text-left text-sm text-slate-600">
            Do you want to mark the selected payments as paid now?
          </DialogPrimitive.Description>
          <div className="flex gap-3">
            <ActionButton
              label="Not yet"
              onPress={onClose}
              disabled={isConfirmingPaymentComplete}
              className="flex-1 rounded-full border-[#d1d5db] bg-white py-2 text-[15px] text-slate-700 hover:bg-slate-50"
            />
            <ActionButton
              label={isConfirmingPaymentComplete ? "" : "Mark as paid"}
              onPress={onConfirmPaymentComplete}
              disabled={isConfirmingPaymentComplete}
              leftIcon={
                isConfirmingPaymentComplete ? (
                  <LoaderIcon size={16} className="animate-spin" />
                ) : null
              }
              className="flex-1 rounded-full border border-[#22c55e] bg-[#ecfdf5] py-2 text-[15px] hover:bg-[#dcfce7]"
              textClassName="text-[#444444]"
            />
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

// Content-only final step for payment dialogs that stay open through handoff.
export function PaymentReturnContent({ busy, amountLabel, people = [], onConfirm, onNotYet }: {
  amountLabel?: string;
  people?: string[];
  busy: boolean; onConfirm: () => void; onNotYet: () => void;
}) {
  const title = React.useRef<HTMLHeadingElement>(null);
  React.useEffect(() => { title.current?.focus(); }, []);
  return <div className="payment-return relative" onKeyDown={(event) => {
    if (event.key === 'Escape' && !busy) { event.stopPropagation(); onNotYet(); }
    if (event.key === 'Tab') {
      const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
      if (!buttons.length) { event.preventDefault(); return; }
      const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
      if (event.shiftKey && index <= 0) { event.preventDefault(); buttons[buttons.length - 1]?.focus(); }
      else if (!event.shiftKey && index === buttons.length - 1) { event.preventDefault(); buttons[0]?.focus(); }
    }
  }}>
    <PaymentDialogClose onClose={onNotYet} disabled={busy} />
    <h2 ref={title} tabIndex={-1} className="handoff-return-title">Did you pay?</h2>
    <p className="payment-return-copy">Confirm once your payment is complete.</p>
    {amountLabel && <div className="payment-return-summary" role="group" aria-label="Payment summary">
      <div className="payment-return-total"><span>{people.length ? `${people.length} ${people.length === 1 ? 'share' : 'shares'}` : 'Total'}</span><strong>{amountLabel}</strong></div>
      {people.length > 0 && <p className="payment-return-people">{people.join(', ')}</p>}
    </div>}
    <div className="payment-return-actions" aria-busy={busy}>
      <ActionButton label={busy ? 'Marking as paid…' : 'Mark as paid'} onPress={onConfirm} disabled={busy} className="handoff-primary handoff-acknowledge" />
      <button type="button" disabled={busy} className="payment-return-later" onClick={onNotYet}>Not yet</button>
    </div>
    <span role="status" className="sr-only">{busy ? 'Marking as paid…' : ''}</span>
  </div>;
}
